import { useSnackbar } from 'notistack5';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCalendarApi } from 'src/api/useCalendarApi';
import { UserEntity, useUserApi } from 'src/api/useUserApi';
import { CREATE_USER_SYSTEM } from 'src/constants';
import { NetworkAccessError } from 'src/errors/errors';
import { useAppDispatch } from 'src/redux/store';
import { API_PATH } from '../../routes/generalPath';
import { deleteCalendarRowData, enqueteCalendarSlice, setClearSearchFlag } from '../store/enqueteCalendarSlice';

export const useCalendarListGrid = () => {
  // Redux dispatch 関数を取得
  const dispatch = useAppDispatch();
  const userApi = useUserApi();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [inProgress, setInProgress] = useState<boolean>(false);
  const startProcess = () => setInProgress(true);
  const stopProcess = () => setInProgress(false);
  // カレンダー関連データ取得 API
  const enqCalendarListApi = useCalendarApi();

  /**
   * アンケートドロップダウンデータ取得
   */
  const getSurveyList = async () => {
    // アンケートドロップダウンデータ取得
    const surveyLList = await enqCalendarListApi.getSurveyList();
    if (surveyLList === undefined) {
      return 1;
    }
    // アンケートの初期化
    dispatch(enqueteCalendarSlice.actions.initSurveyListData(surveyLList));

    return surveyLList;
  };

  /**
   * APIからデータ件数取得
   */
  const getCountLimitedLoadList = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // フィルター条件
    filterModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ) => {
    // カレンダーデータ件数を取得
    const countEnqueteAnswerList = await enqCalendarListApi.getCountLimitedCalendar(surveyIds, year, month, day, filterModel, currentLang);
    if (countEnqueteAnswerList === undefined) {
      return 1;
    }

    return countEnqueteAnswerList;
  };

  /**
   * APIからデータ取得
   */
  const getLimitedLoadList = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // 毎ページ件数
    blockSize: number,
    // 開始行
    startRow: number,
    // ソート条件
    sortModel: any,
    // フィルター条件
    filterModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ) => {
    // カレンダーデータを取得
    const enqueteCalendarList = await enqCalendarListApi.getLimitedList(
      surveyIds,
      year,
      month,
      day,
      blockSize,
      startRow,
      sortModel,
      filterModel,
      currentLang,
    );
    if (enqueteCalendarList === undefined || enqueteCalendarList.length === 0) {
      dispatch(enqueteCalendarSlice.actions.initEnqueteCalendarLimitedListData([]))
      return;
    };
    // 全て作成者のEmail取得
    const emails = enqueteCalendarList.reduce<string[]>((ids, rp) => {
      if (rp.createdUserId !== CREATE_USER_SYSTEM) ids.push(rp.createdUserId);
      return ids;
    }, []);
    // 作成者情報取得
    const users = await userApi.getByEmail(emails ?? [], null);
    const userMap = (users ?? []).reduce<Map<string, UserEntity>>((data, user) => {
      if (user.email) data.set(user.email, user);
      return data;
    }, new Map<string, UserEntity>);
    // 作成者の名称編集
    const newDataList = enqueteCalendarList.map((item) => ({
      ...item,
      userNameJpn: userMap.get(item.createdUserId)?.fullNameJpn ?? '',
      userNameEng: userMap.get(item.createdUserId)?.fullNameEng ?? '',
    }));
    // ストアの初期化
    dispatch(enqueteCalendarSlice.actions.initEnqueteCalendarLimitedListData(newDataList));
  };

  /**
   * 選択行削除
   */
  const deleteRows = async (ids: string[]) => {
    try {
      // 選択するカレンダーを削除
      await enqCalendarListApi.deleteCalendarData(ids);
      // 成功メッセージ：削除しました(Deleted)
      enqueueSnackbar(t('message.deleted'), { variant: 'success' });
    } catch (error) {
      if (error instanceof NetworkAccessError) {
        if (error.isForbidden()) {
          // エラーメッセージ：あなたは管理者でないため、削除に失敗しました(Deletion failed because you are not an administrator)
          enqueueSnackbar(t('enqueteRequest.message.failedDeleteNotAdmin'), { variant: 'error' });
        } else {
          // エラーメッセージ：カレンダー情報の削除に失敗しました(Failed to delete calendar information)
          enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useCalendarApi.failedDeleteData'), { variant: 'error' });
        }
      } else {
        // エラーメッセージ：カレンダー情報の削除に失敗しました(Failed to delete calendar information)
        enqueueSnackbar(t('enqueteCalendarList.apiExceptionMessage.useCalendarApi.failedDeleteData'), { variant: 'error' });
      }
      return;
    }
    // カレンダーデータから削除データを除去する
    dispatch(deleteCalendarRowData(ids));
  };

  /**
   * カレンダー追加
   */
  const goToCalendarCreatePage = () => {
    dispatch(setClearSearchFlag(false));
    // プロセスの開始
    startProcess();
    // 「コンプライアンス・レビュー カレンダー　予定登録」画面に遷移する
    navigate(`${API_PATH.ENQUETE_CALENDAR}/calendar-edit`);
    // プロセスの終了
    stopProcess();
  };

  return { getSurveyList, getLimitedLoadList, getCountLimitedLoadList, deleteRows, goToCalendarCreatePage };
};
